import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listMessages } from "@/lib/content";
import { Loader2, RefreshCw, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminMessages() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { setRows(await listMessages()); }
    catch (e) {
      if (e?.response?.status === 401) { navigate("/login", { replace: true }); return; }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-messages">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink">Outbound Messages</h2>
          <p className="text-sm text-muted-ink mt-1">
            Auto-reply WhatsApp messages. <b className="text-orange-600">Delivery is mocked</b> — connect Twilio to go live.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full" data-testid="messages-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm">
        <b>MOCKED:</b> Messages below are generated and logged but not actually sent. Add Twilio credentials to enable live delivery.
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? (
          <div className="p-16 grid place-items-center text-muted-ink">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl bg-white hairline p-16 text-center text-muted-ink" data-testid="messages-empty">
            No outbound messages yet. Submit a lead on the site to see an auto-reply here.
          </div>
        ) : (
          rows.map((m) => (
            <div
              key={m.id}
              data-testid={`message-row-${m.id}`}
              className="rounded-2xl bg-white hairline shadow-soft p-5"
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="h-10 w-10 rounded-xl bg-green-50 grid place-items-center shrink-0">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-ink font-mono text-sm">{m.to_phone}</div>
                    <span className="inline-flex rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      {m.status}
                    </span>
                    {m.promo_code ? (
                      <span className="inline-flex rounded-full bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 font-mono">
                        {m.promo_code}
                      </span>
                    ) : null}
                    <span className="ml-auto text-xs text-muted-ink">{formatDate(m.created_at)}</span>
                  </div>
                  <pre className="mt-3 text-sm text-ink-2 whitespace-pre-wrap font-sans leading-relaxed">
{m.body}
                  </pre>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
